import { z } from "zod";

const emptyToUndefined = (v: unknown) => (v === "" ? undefined : v);

// Coerces empty / nullish / NaN inputs to `null` for nullable numeric fields.
const emptyToNullNumber = (v: unknown) => {
  if (v === "" || v == null) return null;
  if (typeof v === "number" && isNaN(v)) return null;
  const n = typeof v === "number" ? v : Number(v);
  return isNaN(n) ? null : n;
};

const emptyToNullString = (v: unknown) => (v === "" || v == null ? null : v);

export const dealInputSchema = z.object({
  name: z.string().min(1).max(255),
  stage_id: z.string().uuid(),
  amount: z.preprocess(
    (v) => (v === "" || v == null || (typeof v === "number" && isNaN(v)) ? undefined : Number(v)),
    z.number().positive().optional(),
  ),
  currency: z.string().max(10).default("USD"),
  expected_close_date: z.preprocess(emptyToUndefined, z.string().optional()),
  actual_close_date: z.preprocess(emptyToUndefined, z.string().optional()),
  lost_reason: z.preprocess(emptyToUndefined, z.string().max(500).optional()),
  won_reason: z.preprocess(emptyToUndefined, z.string().max(500).optional()),
  tags: z.array(z.string()).default([]),
  probability_pct_override: z.preprocess(
    emptyToNullNumber,
    z.number().min(0).max(100).nullable().default(null),
  ),
  // Rep assignment (2026-06-03). Reassignment is manager-gated at the RPC.
  ae_user_id: z.preprocess(emptyToNullString, z.string().uuid().nullable().default(null)),
  bdr_user_id: z.preprocess(emptyToNullString, z.string().uuid().nullable().default(null)),

  // Territory — copied from the customer org on create; only revenue_leader
  // may change it (gated at the RPC). Required in the UI forms.
  territory_id: z.preprocess(emptyToNullString, z.string().uuid().nullable().default(null)),

  // Marketing attribution — set/change the deal's campaign + lead source.
  campaign_id: z.preprocess(emptyToNullString, z.string().uuid().nullable().default(null)),
  lead_source_id: z.preprocess(emptyToNullString, z.string().uuid().nullable().default(null)),

  // Editable relationship fields surfaced on the detail form (2026-06-16).
  customer_organization_id: z.preprocess(
    emptyToNullString,
    z.string().uuid().nullable().default(null),
  ),
  owner_user_id: z.preprocess(emptyToNullString, z.string().uuid().nullable().default(null)),
  // Partner / partner-rep moved to the deal_partners record (registrant flag);
  // see manna.deal_partners + PartnersSection (merge 20260617000002).

  // Deal priority (low | medium | high), default medium.
  priority: z.enum(["low", "medium", "high"]).default("medium"),

  // Qualification fields (UI design §4.2.3). Saved via
  // public.manna_update_deal_qualification — see DealDetailPage save handler.
  budget_amount: z.preprocess(emptyToNullNumber, z.number().positive().nullable().default(null)),
  budget_currency: z.preprocess(emptyToNullString, z.string().length(3).nullable().default(null)),
  budget_confidence: z.enum(["unknown", "guessed", "stated", "confirmed"]).default("unknown"),
  budget_notes: z.string().default(""),
  current_problems: z.string().default(""),
  desired_outcomes: z.string().default(""),
  compelling_event_date: z.preprocess(
    emptyToNullString,
    z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected ISO date (YYYY-MM-DD)")
      .nullable()
      .default(null),
  ),
  compelling_event_driver: z.string().default(""),
  urgency: z.enum(["no_deadline", "soft", "hard"]).default("no_deadline"),
  decision_process: z.string().default(""),
});

export type DealInput = z.infer<typeof dealInputSchema>;

// Subset of DealInput that maps to the manna_update_deal_qualification RPC
// payload — used by the split-save handler in DealDetailPage.
export const QUALIFICATION_FIELDS = [
  "budget_amount",
  "budget_currency",
  "budget_confidence",
  "budget_notes",
  "current_problems",
  "desired_outcomes",
  "compelling_event_date",
  "compelling_event_driver",
  "urgency",
  "decision_process",
] as const satisfies readonly (keyof DealInput)[];

export type QualificationField = (typeof QUALIFICATION_FIELDS)[number];

export interface DealDetailRow {
  id: string;
  name: string;
  pipeline_id: string;
  stage_id: string;
  amount: number | null;
  currency: string;
  expected_close_date: string | null;
  actual_close_date: string | null;
  lost_reason: string | null;
  won_reason: string | null;
  tags: string[];
  probability_pct_override: number | null;
  primary_person_id: string | null;
  customer_organization_id: string | null;
  owner_user_id: string | null;
  ae_user_id: string | null;
  bdr_user_id: string | null;
  territory_id: string | null;
  campaign_id: string | null;
  lead_source_id: string | null;
  priority: "low" | "medium" | "high";
  // Qualification fields (mirror manna.deals new columns)
  budget_amount: number | null;
  budget_currency: string | null;
  budget_confidence: "unknown" | "guessed" | "stated" | "confirmed";
  budget_notes: string | null;
  current_problems: string | null;
  desired_outcomes: string | null;
  compelling_event_date: string | null;
  compelling_event_driver: string | null;
  urgency: "no_deadline" | "soft" | "hard";
  decision_process: string | null;
  row_version: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  // Resolved actor names for the read-only Created / Updated info (useDeal).
  created_by_name: string | null;
  updated_by_name: string | null;
  pipeline: { id: string; name: string } | null;
  stage: {
    id: string;
    name: string;
    stage_type: string;
    qualification_phase: string | null;
  } | null;
  primary_person: { id: string; full_name: string } | null;
  customer_organization: { id: string; name: string } | null;
  owner: {
    id: string;
    email: string | null;
    avatar_url: string | null;
    person: { full_name: string } | null;
  } | null;
}
